/**
 * @module builder
 */
import type {
  QuoteDocument,
  TieredPackageContentType,
  TieredPackageLevel,
  TieredPackageTier,
  TieredPackageTierId
} from 'paintscout'
// Deep imports (not the `../../../calculator` barrel): this module is pulled in eagerly by
// `builder/quote/index.ts`, and the calculator barrel re-exports `calculate.ts`, which imports back
// into `builder` — going through the barrel here closes that cycle and can leave sibling builder
// exports (e.g. getCrews) undefined depending on module init order.
import { roundPrice } from '../../../calculator/util/round-price'
import { TIER_ORDER } from './default-tiered-packages'
import { getIncludedFeatureLabels } from './tiered-package-features'
import { getTieredPackageContentType } from './tiered-package-style'
import { getTieredPackageBasePrice, resolveTieredPackageTierPrice } from './resolve-tier-prices'

export interface TierPriceInfo {
  tierId: TieredPackageTierId
  level: TieredPackageLevel
  name: string
  tagline?: string
  features?: string[]
  /** Resolved from the package set — the same value for every tier. */
  contentType?: TieredPackageContentType
  description?: string
  isBase: boolean
  /** Tier is switched off — filtered out of the customer-facing comparison and the review step. */
  isDisabled: boolean
  /** The estimator typed this tier's price, so it holds against a product-swap recalculation. */
  isOverridden: boolean
  isRecommended: boolean
  isSelected: boolean
  /** Signed delta vs the base estimate. */
  delta: number
  /** Resolved total price for the tier. */
  price: number
  /** Signed percent vs the base estimate. */
  percent: number
}

/**
 * Resolve the display price + delta for each tier, relative to the base estimate
 * (the quote subtotal). Pure — does not mutate or recalculate the quote.
 */
export function getTierPrices(args: { quote: QuoteDocument }): Record<TieredPackageTierId, TierPriceInfo> | null {
  const { quote } = args
  const tieredPackages = quote.tieredPackages
  if (!tieredPackages || !tieredPackages.tiers) {
    return null
  }

  // The estimate as built, which is what every tier prices against. With product rules the quote
  // subtotal follows the selected tier, so this reads the base tier's stamped price instead.
  const basePrice = getTieredPackageBasePrice({ quote })
  // One content type for the whole set; the per-tier field is legacy and resolves through here.
  const contentType = getTieredPackageContentType(tieredPackages)

  const resolve = (tierId: TieredPackageTierId): TierPriceInfo => {
    const tier: TieredPackageTier = tieredPackages.tiers[tierId]
    const isBase = tierId === tieredPackages.baseTierId

    // One shared resolver, so a card price always matches what the calculator folds into the total.
    const price = resolveTieredPackageTierPrice({ tieredPackages, tierId, basePrice })

    const delta = roundPrice(price - basePrice)
    const percent = basePrice ? roundPrice((delta / basePrice) * 100) : 0

    return {
      tierId,
      level: tier?.level,
      name: tier?.name,
      tagline: tier?.tagline,
      // Resolved from the shared feature rows (legacy per-tier arrays migrate on read).
      features: getIncludedFeatureLabels(tieredPackages, tierId),
      contentType,
      description: tier?.description,
      isBase,
      isDisabled: !!tier?.disabled,
      isOverridden: !isBase && !!tier?.overridden,
      isRecommended: tierId === tieredPackages.recommendedTierId,
      isSelected: tierId === tieredPackages.selectedTierId,
      delta,
      price,
      percent
    }
  }

  return TIER_ORDER.reduce(
    (acc, tierId) => {
      acc[tierId] = resolve(tierId)
      return acc
    },
    {} as Record<TieredPackageTierId, TierPriceInfo>
  )
}
